import React, { useEffect, useRef, useState } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import GooglePay from 'utils/GooglePay';
import { buttons, mediaQueries } from 'style/config';

/**
 * Google Pay pill button (Release 4 / Checkout).
 *
 * Renders Google's SDK-painted button inside a 44px pill container.
 *
 * Caller contract: this component assumes the parent has already decided that
 * Google Pay should be shown — e.g. `NewUserPaymentView` only mounts this pill
 * after passing the locale killswitch, the `isGooglePayEnabledCheckout` config
 * flag, and a successful `initPaymentsClient()` call. The pill does NOT re-run
 * those checks, to avoid duplicate SDK probes on the render path. See PAY-6993
 * for the eventual checkout-scoped eligibility helper that will replace the
 * interim parent gating.
 */
const SDK_BUTTON_STATUS = {
    PENDING: 'pending',
    MOUNTED: 'mounted',
    FAILED: 'failed'
};

const GooglePayPill = ({ transactionAmount, isBopis, handleGooglePayClick = () => {}, onRenderFailure = () => {} }) => {
    const containerRef = useRef(null);
    const [sdkButtonStatus, setSdkButtonStatus] = useState(SDK_BUTTON_STATUS.PENDING);

    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }

        let buttonNode;

        try {
            buttonNode = GooglePay.getPaymentsClient().createButton({
                onClick: handleGooglePayClick,
                buttonType: 'plain',
                buttonColor: 'black',
                buttonSizeMode: 'fill',
                buttonRadius: buttons.HEIGHT / 2
            });
            containerRef.current.appendChild(buttonNode);
            setSdkButtonStatus(SDK_BUTTON_STATUS.MOUNTED);
        } catch (err) {
            Sephora.logger.error('[GooglePay] createButton failed', err);
            setSdkButtonStatus(SDK_BUTTON_STATUS.FAILED);
            onRenderFailure(err);

            return undefined;
        }

        return () => {
            if (buttonNode && buttonNode.parentNode === containerRef.current) {
                containerRef.current.removeChild(buttonNode);
            }
        };
    }, [handleGooglePayClick, onRenderFailure]);

    if (sdkButtonStatus === SDK_BUTTON_STATUS.FAILED) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            data-at={Sephora.debug.dataAt('checkout_googlepay_btn')}
            css={containerStyle}
            data-transaction-amount={transactionAmount}
            data-is-bopis={isBopis ? 'true' : 'false'}
        />
    );
};

const containerStyle = {
    display: 'flex',
    width: '100%',
    height: buttons.HEIGHT,
    // The SDK-injected child is a flex item whose default min-width: auto
    // would set the parent to its largest width
    minWidth: 0,
    [mediaQueries.sm]: {
        width: '151px'
    },
    [mediaQueries.md]: {
        width: '100%'
    },
    '& .gpay-button-fill': {
        width: '100%',
        height: buttons.HEIGHT,
        minHeight: buttons.HEIGHT,
        minWidth: 0
    }
};

export default wrapFunctionalComponent(GooglePayPill, 'GooglePayPill');
